import React from 'react';
import { Cell } from '@/pages/dashboard/components/CommonTable/components/CommonTableCell/Cell';
import type { IColumn } from '@/pages/dashboard/components/CommonTable/type';
import type {
  BuildMetricCellContextParams,
  BuildPrMetricColumnsParams,
  INestedMetricValue,
  PrMediaCellRenderContext,
  PrMediaTableProps,
  StackedMetricBarParams,
} from '../_type';
import { STREAM_METRIC_CODES } from '../_constants';
import { formatValueForRule, getChannelColor, getMetricBarPercent, isNestedMetricValue, mergeFeatures } from '../_utils';

function buildMetricCellContext(p: BuildMetricCellContextParams): PrMediaCellRenderContext {
  const {
    value,
    record,
    rowIndex,
    isEN,
    channels,
    colMaxes,
    formatRule,
  } = p;

  return {
    value,
    record,
    rowIndex,
    isEN,
    channels,
    colMaxes: { ...colMaxes },
    formatMetric: raw => formatValueForRule(raw, formatRule),
  };
}

function renderStackedMetricBar(p: StackedMetricBarParams): JSX.Element {
  const {
    entries,
    totalNum,
    columnLabel,
    formatRule,
    colorByCodeProp,
    setStackedBarTooltip,
  } = p;

  return (
    <div
      className="dbh-pr-media-table__stacked-bar-wrap"
      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setStackedBarTooltip({
          entries: entries.map((item, idx) => ({
            ...item,
            color: getChannelColor(item.channel, idx, colorByCodeProp),
          })),
          total: totalNum,
          columnLabel,
          formatRule,
          position: { x: rect.left + rect.width / 2, y: rect.top },
        });
      }}
      onMouseLeave={() => setStackedBarTooltip(null)}
    >
      {entries.map((item, idx) => {
        const pct = totalNum > 0 ? Math.max(1, (item.value / totalNum) * 100) : 0;
        return (
          <div
            key={item.channel}
            className="dbh-pr-media-table__stacked-bar-segment"
            style={{
              width: `${pct}%`,
              backgroundColor: getChannelColor(item.channel, idx, colorByCodeProp),
            }}
          />
        );
      })}
    </div>
  );
}

function getEntriesByChannel(
  byChannel:
  | Record<string, number>
  | Array<{ code: string; value: number; }>
  | Record<string, unknown>,
): Array<{ channel: string; value: number; }> {
  const entries: Array<{ channel: string; value: number; }> = Array.isArray(byChannel)
    ? byChannel
      .filter(item => item?.value !== null && item?.value !== undefined && Number(item.value) > 0)
      .map(item => ({ channel: String(item.code), value: Number(item.value) }))
    : Object.entries(byChannel)
      .filter(([, v]) => Number(v) > 0)
      .map(([ch, v]) => ({ channel: ch, value: Number(v) }));

  entries.sort((a, b) => b.value - a.value);
  return entries;
}

export function buildPrMetricColumns(params: BuildPrMetricColumnsParams): IColumn[] {
  const {
    tableMode,
    isEN,
    channels,
    colMaxes,
    colorByCodeProp,
    cellRenderers,
    ov,
    activeMetricCodes,
    metricNameEnMap,
    metricNameCnMap,
    defaultMetricFeaturesBase,
    getColumnFeaturesExtra,
    engagementFeatureMerged,
    setStackedBarTooltip,
  } = params;

  if (tableMode === 'articles') {
    return [];
  }

  if (tableMode === 'posts' || tableMode === 'videos') {
    const oEng = ov.get('engagement');
    const engFeat = engagementFeatureMerged as IColumn['features'] & { format?: string; };
    const nameEn = String(oEng?.nameEn ?? oEng?.name ?? 'Engagement');
    const nameCn = String(oEng?.nameCn ?? oEng?.name ?? '互动量');
    const fmtRule = (engFeat.format as string) || 'thousandsInit';
    const engagementColumn: IColumn = {
      code: 'engagement',
      name: nameEn,
      nameEn,
      nameCn,
      align: (oEng?.align as IColumn['align']) || 'right',
      width: oEng?.width ?? 120,
      features: engFeat as IColumn['features'],
      getValue: (record: Record<string, unknown>) => {
        const n = Number(record.engagement);
        return Number.isFinite(n) ? n : 0;
      },
    };

    return [{
      ...engagementColumn,
      render: value => (
        <Cell className="dbh-pr-media-table__metric-cell" config={{ column: engagementColumn }}>
          <div className="dbh-pr-media-table__metric-value">
            {formatValueForRule(value, fmtRule)}
          </div>
        </Cell>
      ),
    }];
  }

  return activeMetricCodes.map(code => {
    const oM = ov.get(code);
    const featObj = mergeFeatures(
      defaultMetricFeaturesBase,
      getColumnFeaturesExtra(String(code)),
    ) as IColumn['features'] & { format?: string; };
    const mEn = String((oM?.nameEn ?? oM?.name ?? metricNameEnMap[code]) || code);
    const mCn = String((oM?.nameCn ?? oM?.name ?? metricNameCnMap[code]) || code);
    const formatRule = (featObj.format as string) || 'thousandsInit';

    const col: IColumn = {
      code,
      name: mEn,
      nameEn: mEn,
      nameCn: mCn,
      align: (oM?.align as IColumn['align']) || 'right',
      width: oM?.width ?? 140,
      features: featObj as IColumn['features'],
      getValue: (record: Record<string, unknown>) => {
        const raw = record[code];
        if (isNestedMetricValue(raw)) return raw.total;
        const n = Number(raw);
        return Number.isFinite(n) ? n : 0;
      },
      render: (value, record, rowIndex) => {
        const ctx = buildMetricCellContext({
          value,
          record,
          rowIndex,
          isEN,
          channels,
          colMaxes,
          formatRule,
        });
        const renderer = cellRenderers?.[code as keyof NonNullable<PrMediaTableProps['cellRenderers']>];
        if (renderer) {
          return <Cell config={{ column: col }}>{renderer(ctx)}</Cell>;
        }

        if (
          tableMode === 'streams' &&
          (STREAM_METRIC_CODES as readonly string[]).includes(String(code))
        ) {
          const rawPlain = record[code];
          const displaySrc = isNestedMetricValue(rawPlain as unknown)
            ? (rawPlain as INestedMetricValue).total
            : rawPlain;
          const plainText = formatValueForRule(displaySrc, formatRule);
          return (
            <Cell className="dbh-pr-media-table__metric-cell" config={{ column: col }}>
              <div className="dbh-pr-media-table__metric-value">{plainText}</div>
            </Cell>
          );
        }

        const raw = record[code];

        if (isNestedMetricValue(raw as unknown)) {
          const nested = raw as unknown as INestedMetricValue;
          const totalNum = Number(nested.total) || 0;
          const text = formatValueForRule(totalNum, formatRule);
          const entries = getEntriesByChannel(nested.by_channel as Record<string, unknown>);
          const columnLabel = isEN ? mEn : mCn;

          return (
            <Cell className="dbh-pr-media-table__metric-cell" config={{ column: col }}>
              <div className="dbh-pr-media-table__metric-value">{text}</div>
              {renderStackedMetricBar({
                entries,
                totalNum,
                columnLabel,
                formatRule,
                colorByCodeProp,
                setStackedBarTooltip,
              })}
            </Cell>
          );
        }

        const num = Number(raw);
        const max = colMaxes[code] ?? 0;
        const text = formatValueForRule(raw, formatRule);
        const byChannelRaw = record[`${code}__by_channel`] as
          | Record<string, number>
          | Array<{ code: string; value: number; }>
          | undefined;

        if (byChannelRaw && typeof byChannelRaw === 'object') {
          const totalNum = num || 0;
          const entries = getEntriesByChannel(byChannelRaw);
          const columnLabel = isEN ? mEn : mCn;

          return (
            <Cell className="dbh-pr-media-table__metric-cell" config={{ column: col }}>
              <div className="dbh-pr-media-table__metric-value">{text}</div>
              {renderStackedMetricBar({
                entries,
                totalNum,
                columnLabel,
                formatRule,
                colorByCodeProp,
                setStackedBarTooltip,
              })}
            </Cell>
          );
        }

        const pct = getMetricBarPercent(num, max);

        return (
          <Cell className="dbh-pr-media-table__metric-cell" config={{ column: col }}>
            <div className="dbh-pr-media-table__metric-value">{text}</div>
            <div className="dbh-pr-media-table__metric-bar-wrap">
              <div className="dbh-pr-media-table__metric-bar" style={{ width: `${pct}%` }} />
            </div>
          </Cell>
        );
      },
    };

    return col;
  });
}
